<!-- https://anjaneyasivan.github.io/v-suggestions/ -->

<template>
  <div class="typeahead">
    <input
      :id="id"
      v-model="query"
      type="text"
      class="form-control typeahead__input"
      :class="classes"
      v-bind="$attrs"
      :placeholder="placeholder"
      role="combobox"
      aria-autocomplete="both"
      aria-owns="id + '_results'"
      :aria-expanded="showItems"
      :aria-describedby="id + '_aria-descr'"
      :disabled="disabled"
      @keydown="onKeyDown"
      @blur="hideItems"
      @focus="showResults"
      @input="onQueryChanged($event.target.value)"
    />

    <ul
      v-show="showItems"
      :id="id + '_results'"
      class="typeahead__drop-down dropdown-menu"
      role="listbox"
    >
      <li
        v-for="(item, index) in items"
        :key="index"
        class="typeahead__item"
        :class="{ 'typeahead__item--active': index === activeItemIndex }"
        role="presentation"
        @click.prevent="selectItem(index)"
        @mouseover="highlightItem(index)"
      >
        <slot name="item" :item="item">
          <a
            role="option"
            href="#"
            :title="getText(item)"
            v-html="getListText(item)"
          >
          </a>
        </slot>
      </li>
    </ul>

    <div :id="id + '_aria-descr'" class="sr-only">
      <span v-if="minLength > 1">Wpisz co najmniej {{ minLength }} znaków</span>
      Po rozwinięciu użyj strzałek góra dół, aby zaznaczyć opcję Użyj klawisza
      enter, aby wybrać
    </div>

    <div aria-live="assertive" class="sr-only">
      {{ ariaResults }}
    </div>
  </div>
</template>

<script>
import debounce from "debounce";
import BaseControl from "./BaseControl";

export default {
  name: "BaseTypeahead",
  extends: BaseControl,
  inheritAttributes: true,
  props: {
    id: {
      type: String,
      required: true
    },
    getValue: {
      type: Function,
      default: item => item.id
    },
    getText: {
      type: Function,
      default: item => item.name
    },
    getListText: {
      type: Function,
      default: item => item.name
    },
    value: {
      type: String,
      required: true
    },
    options: {
      type: Array,
      default: () => []
    },
    text: {
      type: String,
      default: ""
    },
    minLength: {
      type: Number,
      default: 3
    },
    selectFirst: {
      type: Boolean,
      default: true
    },
    autoSelect: {
      type: Boolean,
      default: true
    },
    ariaDD: {
      type: String,
      default: "Wybierz stację używając strzałek góra/dół"
    },
    placeholder: {
      type: String,
      default: ""
    },
    onInputChange: {
      type: Function,
      default(query) {
        let found = this.allItems.filter(o =>
          Object.keys(o).some(k =>
            o[k].toLowerCase().includes(query.toLowerCase())
          )
        );
        return new Promise(resolve => {
          resolve(found);
        });
      }
    }
  },
  data() {
    return {
      query: this.text || this.value,
      // lastQuery: "",
      allItems: [],
      items: [],
      activeItemIndex: -1,
      selectedItemIndex: -1,
      showItems: false,
      isSelected: false,
      ariaResults: "",
      loading: false
    };
  },
  computed: {
    selectedItem() {
      return typeof this.items[this.selectedItemIndex] !== "undefined"
        ? this.items[this.selectedItemIndex]
        : {};
    }
  },
  watch: {
    value(newValue, oldValue) {
      if (newValue != oldValue) {
        this.query = newValue;
      }
    }
  },
  created() {
    this.allItems = this.options || [];
  },
  methods: {
    onItemSelected() {
      let value = this.getValue(this.selectedItem);
      let text = this.getText(this.selectedItem);

      this.$emit("input", text || this.query);
      this.$emit("update:value", value || -1);
      this.$emit("update:item", { ...this.selectedItem });
    },

    selectItem(index, hide) {
      hide = hide === false ? false : true;
      this.isSelected = true;
      this.selectedItemIndex = index;
      this.activeItemIndex = index;
      if (index > -1) {
        this.query = this.getText(this.selectedItem);
      }
      this.onItemSelected();
      if (hide) {
        this.hideItems();
      }
    },

    resetItem(value) {
      this.selectedItemIndex = -1;
      this.activeItemIndex = -1;
      this.isSelected = false;
      this.onItemSelected();
    },

    setItems(items) {
      this.items = items;
      this.activeItemIndex = -1;
      this.showResults();
      // if (this.selectFirst) {
      //   this.selectItem(0, false)
      // }
      this.announceResults();
    },

    async onQueryChanged(value) {
      // if (value === this.lastQuery) {
      //   return;
      // }

      this.resetItem(value);

      this.v && this.v.$touch();

      if (value.trim().length < this.minLength) {
        return;
      }

      this.loading = true;

      const items = await this.onInputChange(value);

      this.setItems(items);
      // this.lastQuery = value;
      this.loading = false;
    },

    hideItems() {
      setTimeout(() => {
        this.showItems = false;
        if (!this.isSelected && this.selectFirst) {
          this.selectItem(0, false);
        }
      }, 150);
    },

    showResults() {
      if (this.showItems) return;

      this.showItems =
        this.query &&
        this.query.length >= this.minLength &&
        this.items.length > 0;

      this.activeItemIndex = this.selectedItemIndex;
    },

    /*

    NAVIGATION

    */

    onKeyDown(e) {
      this.showResults();
      switch (e.keyCode) {
        case 40:
          this.highlightItem("down");
          if (this.autoSelect) {
            this.selectItem(this.activeItemIndex, false);
          }
          e.preventDefault();
          break;
        case 38:
          this.highlightItem("up");
          if (this.autoSelect) {
            this.selectItem(this.activeItemIndex, false);
          }
          e.preventDefault();
          break;
        case 13:
          this.selectItem(this.activeItemIndex, true);
          e.preventDefault();
          break;
        default:
          return true;
      }
    },

    highlightItem(direction) {
      if (this.items.length === 0) {
        return;
      }

      if (typeof direction === "number") {
        this.activeItemIndex = direction;
        return;
      }

      if (direction === "down") {
        this.activeItemIndex++;
        if (this.activeItemIndex === this.items.length) {
          this.activeItemIndex = 0;
        }
      } else {
        this.activeItemIndex--;
        if (this.activeItemIndex < 0) {
          this.activeItemIndex = this.items.length - 1;
        }
      }
    },

    /*

    ARIA

    */

    announceResults: debounce(function() {
      let s = "";
      if (this.isSelected) {
        s = "Wybrano " + this.queryValue;
      } else if (this.items.length > 0 && this.showItems && !this.loading) {
        s =
          "Znaleziono " +
          this.items.length +
          " wyników Pierwszy " +
          this.items[0].n;
      } else if (
        this.items.length === 0 &&
        this.query.length >= this.minLength &&
        !this.loading
      ) {
        s = "Nic nie znaleziono";
      }
      this.ariaResults = s;
    }, 100)
  }
};
</script>

<style lang="scss">
@import "@/styles/_variables";
.typeahead {
  position: relative;
  .typeahead__drop-down {
    padding: 0;
    display: block;
    margin-top: -1px;
    width: 100%;
    max-height: 350px;
    overflow-y: scroll;
    box-shadow: 0px 3px 5px rgba(0, 0, 0, 0.2);
  }
  .typeahead__item {
    padding: 12px;
    font-size: 0.85em;
    a {
      color: inherit;
    }

    &:hover {
      background: $col-polmed-blue;
      color: white;
    }

    &--active {
      background: #f3f3f3;
      color: $col-polmed-navy;
      font-weight: 700;
      &:hover {
        color: white;
        background: $col-polmed-navy;
      }
    }
  }
}
</style>
